import React, { useState, useEffect } from 'react';
import { Plus, X, Building2, CreditCard, TrendingUp, Briefcase, Loader2 } from 'lucide-react';
import type { BankAccount, Financing } from '../../types/company';
import {
  DEVISES,
  TYPES_COMPTE,
  TYPES_FINANCEMENT,
  TYPES_LEVEE_TOUR,
  typesForSection,
  type Devise,
  type FinancingSection,
  type TypeFinancement,
} from '../../utils/financialRecords';

/**
 * Saisie des comptes bancaires et des financements d'une entreprise. Les
 * éléments ont la forme du DTO customer-service (BankAccountDto,
 * FinancingDto) ; la relecture des anciennes formes se fait en amont, dans
 * utils/financialRecords.
 */
type CommonProps = {
  label: string;
  disabled?: boolean;
  companyType?: 'traditional' | 'startup';
};

type FinancialInputProps = CommonProps & (
  | { type: 'bank-accounts'; value: BankAccount[]; onSave: (data: BankAccount[]) => void | Promise<void> }
  | { type: FinancingSection; value: Financing[]; onSave: (data: Financing[]) => void | Promise<void> }
);

/** Champs tels que tapés, avant conversion. */
type Draft = Record<string, string>;

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-transparent';

const formatCurrency = (amount: number, currency: string) => {
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : 'FC';
  return `${symbol} ${amount.toLocaleString()}`;
};

const toNumber = (v: string | undefined): number | undefined => {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) ? n : undefined;
};

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'md:col-span-2' : undefined}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

interface EditableListProps<T extends { id: string }> {
  title: string;
  icon: React.ReactNode;
  addTitle: string;
  value: T[];
  onSave: (items: T[]) => void | Promise<void>;
  disabled: boolean;
  /** Construit l'élément depuis le brouillon, ou null s'il manque un champ requis. */
  build: (draft: Draft) => T | null;
  renderForm: (draft: Draft, set: (key: string, value: string) => void) => React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
}

/** Liste éditable : copie locale, ajout, retrait, puis une seule sauvegarde. */
function EditableList<T extends { id: string }>({
  title, icon, addTitle, value, onSave, disabled, build, renderForm, renderItem,
}: EditableListProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [items, setItems] = useState<T[]>(value);
  const [draft, setDraft] = useState<Draft>({});

  // Copie profonde à chaque changement venant du parent (hors édition), pour
  // éviter toute mutation croisée entre instances.
  useEffect(() => {
    if (!isEditing) setItems((value || []).map((item) => ({ ...item })));
  }, [value, isEditing]);

  const reset = () => {
    setItems((value || []).map((item) => ({ ...item })));
    setDraft({});
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(items.map((item) => ({ ...item })));
      setIsEditing(false);
      setDraft({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde financière:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const candidate = build(draft);
  const set = (key: string, v: string) => setDraft((prev) => ({ ...prev, [key]: v }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {icon}
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            {items.length} élément{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        {!disabled && (
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => { setIsEditing(true); reset(); }}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-[#197ca8] bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8]"
              >
                Gérer
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setIsEditing(false); reset(); }}
                  disabled={isSaving}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    'Sauvegarder'
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">{renderItem(item)}</div>
                {isEditing && (
                  <button
                    onClick={() => setItems((prev) => prev.filter((i) => i.id !== item.id))}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Briefcase className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>Aucun élément ajouté</p>
        </div>
      )}

      {isEditing && (
        <div className="border-t border-gray-200 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-4">
              <Plus className="w-5 h-5 text-[#197ca8] mr-2" />
              <h4 className="text-md font-medium text-gray-900">{addTitle}</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{renderForm(draft, set)}</div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  if (!candidate) return;
                  setItems((prev) => [...prev, candidate]);
                  setDraft({});
                }}
                disabled={!candidate}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeviseSelect({ draft, set }: { draft: Draft; set: (k: string, v: string) => void }) {
  return (
    <Field label="Devise">
      <select value={draft.devise || 'USD'} onChange={(e) => set('devise', e.target.value)} className={inputClass}>
        {DEVISES.map((d) => (
          <option key={d.value} value={d.value}>{d.label}</option>
        ))}
      </select>
    </Field>
  );
}

const asDevise = (v: string | undefined): Devise => DEVISES.find((d) => d.value === v)?.value ?? 'USD';
const typeLabel = (t: TypeFinancement) => TYPES_FINANCEMENT.find((o) => o.value === t)?.label ?? t;

const FinancialInput: React.FC<FinancialInputProps> = (props) => {
  const { label, disabled = false, companyType } = props;
  const isStartup = companyType === 'startup';
  const icon = (Icon: typeof Building2) => <Icon className="w-5 h-5 text-[#197ca8]" />;

  if (props.type === 'bank-accounts') {
    return (
      <EditableList<BankAccount>
        title={isStartup ? 'Comptes bancaires et fintech' : label}
        icon={icon(Building2)}
        addTitle="Ajouter un compte bancaire"
        value={props.value}
        onSave={props.onSave}
        disabled={disabled}
        build={(d) => {
          if (!d.nomBanque || !d.numeroCompte) return null;
          return {
            id: `bank_${Date.now()}`,
            nomBanque: d.nomBanque,
            intituleCompte: d.intituleCompte || '',
            numeroCompte: d.numeroCompte,
            devise: asDevise(d.devise),
            typeCompte: TYPES_COMPTE.find((t) => t.value === d.typeCompte)?.value ?? 'professionnel',
          };
        }}
        renderForm={(d, set) => (
          <>
            <Field label="Nom de la banque *">
              <input type="text" value={d.nomBanque || ''} onChange={(e) => set('nomBanque', e.target.value)} className={inputClass} placeholder="Ex: BCDC, TMB, etc." />
            </Field>
            <Field label="Intitulé du compte">
              <input type="text" value={d.intituleCompte || ''} onChange={(e) => set('intituleCompte', e.target.value)} className={inputClass} placeholder="Ex: Compte principal entreprise" />
            </Field>
            <Field label="Numéro de compte *">
              <input type="text" value={d.numeroCompte || ''} onChange={(e) => set('numeroCompte', e.target.value)} className={inputClass} placeholder="Numéro de compte" />
            </Field>
            <DeviseSelect draft={d} set={set} />
            <Field label="Type de compte" wide>
              <select value={d.typeCompte || 'professionnel'} onChange={(e) => set('typeCompte', e.target.value)} className={inputClass}>
                {TYPES_COMPTE.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
          </>
        )}
        renderItem={(account) => (
          <>
            <div className="flex items-center mb-2">
              <Building2 className="w-4 h-4 text-[#197ca8] mr-2" />
              <span className="font-medium text-gray-900">{account.nomBanque}</span>
              <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">{account.devise}</span>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Compte:</strong> {account.numeroCompte}</p>
              {account.intituleCompte && <p><strong>Intitulé:</strong> {account.intituleCompte}</p>}
              {account.typeCompte && (
                <p><strong>Type:</strong> {TYPES_COMPTE.find((t) => t.value === account.typeCompte)?.label}</p>
              )}
            </div>
          </>
        )}
      />
    );
  }

  const section = props.type;
  const isLevee = section === 'funding-rounds';
  const options = typesForSection(section, isStartup);
  const defaultType = options[0].value;

  return (
    <EditableList<Financing>
      title={isLevee ? label : isStartup ? 'Dettes et obligations' : label}
      icon={icon(isLevee ? TrendingUp : CreditCard)}
      addTitle={isLevee ? 'Ajouter une levée de fonds' : 'Ajouter un financement'}
      value={props.value}
      onSave={props.onSave}
      disabled={disabled}
      build={(d) => {
        const montantTotal = toNumber(d.montantTotal);
        const typeFinancement = options.find((o) => o.value === d.typeFinancement)?.value ?? defaultType;
        if (!montantTotal || (!isLevee && !d.objetFinancement)) return null;
        const item: Financing = {
          id: `${isLevee ? 'round' : 'loan'}_${Date.now()}`,
          typeFinancement,
          organisme: d.organisme || '',
          montantTotal,
          devise: asDevise(d.devise),
        };
        if (d.objetFinancement) item.objetFinancement = d.objetFinancement;
        if (d.dateObtention) item.dateObtention = d.dateObtention;
        if (isLevee) {
          const tour = TYPES_LEVEE_TOUR.find((t) => t.value === d.typeLevee)?.value;
          if (tour) item.typeLevee = tour;
          const pre = toNumber(d.valorisationPreMoney);
          if (pre !== undefined) item.valorisationPreMoney = pre;
          const post = toNumber(d.valorisationPostMoney);
          if (post !== undefined) item.valorisationPostMoney = post;
        } else {
          const restant = toNumber(d.montantRestant);
          if (restant !== undefined) item.montantRestant = restant;
          const taux = toNumber(d.tauxInteret);
          if (taux !== undefined) item.tauxInteret = taux;
          const mensualite = toNumber(d.mensualite);
          if (mensualite !== undefined) item.mensualite = mensualite;
          if (d.dateEcheance) item.dateEcheance = d.dateEcheance;
        }
        return item;
      }}
      renderForm={(d, set) => (
        <>
          <Field label={isLevee ? 'Type de levée *' : 'Type de financement'}>
            <select value={d.typeFinancement || defaultType} onChange={(e) => set('typeFinancement', e.target.value)} className={inputClass}>
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label={isLevee ? 'Montant levé *' : 'Montant total *'}>
            <input type="number" value={d.montantTotal || ''} onChange={(e) => set('montantTotal', e.target.value)} className={inputClass} placeholder="0" />
          </Field>
          {!isLevee && (
            <>
              <Field label="Objet du financement *">
                <input type="text" value={d.objetFinancement || ''} onChange={(e) => set('objetFinancement', e.target.value)} className={inputClass} placeholder="Ex: Équipement, fonds de roulement" />
              </Field>
              <Field label="Prêteur">
                <input type="text" value={d.organisme || ''} onChange={(e) => set('organisme', e.target.value)} className={inputClass} placeholder="Nom de la banque ou institution" />
              </Field>
              <Field label="Encours (montant restant)">
                <input type="number" value={d.montantRestant || ''} onChange={(e) => set('montantRestant', e.target.value)} className={inputClass} placeholder="0" />
              </Field>
              <Field label="Taux d'intérêt (%)">
                <input type="number" step="0.01" value={d.tauxInteret || ''} onChange={(e) => set('tauxInteret', e.target.value)} className={inputClass} placeholder="0.00" />
              </Field>
              <Field label="Mensualité">
                <input type="number" value={d.mensualite || ''} onChange={(e) => set('mensualite', e.target.value)} className={inputClass} placeholder="0" />
              </Field>
            </>
          )}
          <DeviseSelect draft={d} set={set} />
          <Field label={isLevee ? 'Date de la levée' : 'Date d\'obtention'}>
            <input type="date" value={d.dateObtention || ''} onChange={(e) => set('dateObtention', e.target.value)} className={inputClass} />
          </Field>
          {!isLevee && (
            <Field label="Date d'échéance">
              <input type="date" value={d.dateEcheance || ''} onChange={(e) => set('dateEcheance', e.target.value)} className={inputClass} />
            </Field>
          )}
          {isLevee && (
            <>
              <Field label="Type de tour">
                <select value={d.typeLevee || ''} onChange={(e) => set('typeLevee', e.target.value)} className={inputClass}>
                  <option value="">Non précisé</option>
                  {TYPES_LEVEE_TOUR.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Valorisation pré-money">
                <input type="number" value={d.valorisationPreMoney || ''} onChange={(e) => set('valorisationPreMoney', e.target.value)} className={inputClass} placeholder="0" />
              </Field>
              <Field label="Valorisation post-money">
                <input type="number" value={d.valorisationPostMoney || ''} onChange={(e) => set('valorisationPostMoney', e.target.value)} className={inputClass} placeholder="0" />
              </Field>
              <Field label="Investisseurs" wide>
                <input type="text" value={d.organisme || ''} onChange={(e) => set('organisme', e.target.value)} className={inputClass} placeholder="Ex: Business angel A, fonds B" />
              </Field>
              <Field label="Utilisation des fonds" wide>
                <textarea value={d.objetFinancement || ''} onChange={(e) => set('objetFinancement', e.target.value)} rows={3} className={inputClass} placeholder="Décrivez comment les fonds ont été ou seront utilisés..." />
              </Field>
            </>
          )}
        </>
      )}
      renderItem={(f) => (
        <>
          <div className="flex items-center mb-2">
            {isLevee
              ? <TrendingUp className="w-4 h-4 text-[#197ca8] mr-2" />
              : <CreditCard className="w-4 h-4 text-[#197ca8] mr-2" />}
            <span className="font-medium text-gray-900">
              {isLevee ? typeLabel(f.typeFinancement) : f.objetFinancement || typeLabel(f.typeFinancement)}
            </span>
            <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
              {formatCurrency(f.montantTotal, f.devise)}
            </span>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            {f.organisme && <p><strong>{isLevee ? 'Investisseurs' : 'Prêteur'}:</strong> {f.organisme}</p>}
            {!isLevee && <p><strong>Type:</strong> {typeLabel(f.typeFinancement)}</p>}
            {f.montantRestant !== undefined && <p><strong>Encours:</strong> {formatCurrency(f.montantRestant, f.devise)}</p>}
            {!!f.tauxInteret && <p><strong>Taux:</strong> {f.tauxInteret}%</p>}
            {f.dateObtention && (
              <p><strong>{isLevee ? 'Date' : 'Période'}:</strong> {f.dateObtention}{f.dateEcheance ? ` au ${f.dateEcheance}` : ''}</p>
            )}
            {isLevee && f.typeLevee && (
              <p><strong>Tour:</strong> {TYPES_LEVEE_TOUR.find((t) => t.value === f.typeLevee)?.label}</p>
            )}
            {isLevee && (f.valorisationPreMoney !== undefined || f.valorisationPostMoney !== undefined) && (
              <p>
                <strong>Valorisation:</strong>{' '}
                {f.valorisationPreMoney !== undefined ? formatCurrency(f.valorisationPreMoney, f.devise) : 'n.c.'} (pré)
                {' / '}
                {f.valorisationPostMoney !== undefined ? formatCurrency(f.valorisationPostMoney, f.devise) : 'n.c.'} (post)
              </p>
            )}
            {isLevee && f.objetFinancement && <p><strong>Utilisation:</strong> {f.objetFinancement}</p>}
          </div>
        </>
      )}
    />
  );
};

export default FinancialInput;
